import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-rotate';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import PanToolIcon from '@material-ui/icons/PanTool';
import CreateIcon from '@material-ui/icons/Create';
import OpenWithIcon from '@material-ui/icons/OpenWith';
import ControlCameraIcon from '@material-ui/icons/ControlCamera';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import ExploreIcon from '@material-ui/icons/Explore';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import LocationOffIcon from '@material-ui/icons/LocationOff';
import cn from 'classnames';
import TapeDrawPad from './TapeDrawPad';
import {
  TYPE_COLORS,
  edgeLengths,
  getFeatureTypeCode,
  getGarageKindName,
  labelAnchor,
  livingSqft,
  measureSqft,
  orthoSnap,
  roundPt,
  sectionAbbrev,
  hitFeature,
  snapToExisting,
  step,
  stretchEdge,
} from './tapeUtils';
import { latLngToFeet, feetToLatLng } from './geo';
import styles from './AerialCanvas.styles';

const useStyles = makeStyles(styles);

// Bearing-independent screen <-> lat/lng conversion. Uses only Leaflet's raw
// Mercator projection (project/unproject/getCenter/getZoom/getSize) — none of
// which the leaflet-rotate plugin patches — so the sketch's screen position
// never depends on the map's current rotation, even though the plugin rotates
// the tile layer underneath it. This is what lets the sketch stay visually
// fixed while the imagery is rotated to align a non-north-aligned house.
// `size` is the overlay's own live-measured {x, y} pixel box (from
// getBoundingClientRect), not map.getSize() — Leaflet's cached container size
// can briefly lag the actual DOM box even while tiles render correctly off of
// it, and using the same freshly-measured rect for both the projection math
// and the SVG's own width/height keeps the two mathematically consistent.
function toScreen(map, latlng, size) {
  const z = map.getZoom();
  const w = map.project(latlng, z);
  const c = map.project(map.getCenter(), z);
  return { x: size.x / 2 + (w.x - c.x), y: size.y / 2 + (w.y - c.y) };
}

function fromScreen(map, pt, size) {
  const z = map.getZoom();
  const c = map.project(map.getCenter(), z);
  return map.unproject(L.point(c.x + (pt.x - size.x / 2), c.y + (pt.y - size.y / 2)), z);
}

const AerialCanvas = ({
  center,
  origin,
  zoomLevel,
  initialBearing,
  features,
  selectedId,
  tapeConfig,
  onCommit,
  onSelect,
  onMove,
  onMoveStart,
  onClearFeatures,
  onDelete,
  onUndoFeatures,
  onRedoFeatures,
  onShiftAll,
  onBearingChange,
}) => {
  const classes = useStyles();
  const el = useRef(null);
  const wrap = useRef(null);
  const mapRef = useRef(null);
  const centerDotRef = useRef(null);
  const centerDotHaloRef = useRef(null);

  const [showCenterDot, setShowCenterDot] = useState(true);
  const [draft, setDraft] = useState([]);
  const [intent, setIntent] = useState('pan');
  const [dir, setDir] = useState('E');
  const [length, setLength] = useState('24');
  const [width, setWidth] = useState('40');
  const [depth, setDepth] = useState('28');
  const [edit, setEdit] = useState(null);
  const editRef = useRef(null);
  editRef.current = edit;
  const [moveDelta, setMoveDelta] = useState(null);
  const [viewSize, setViewSize] = useState({ w: 800, h: 600 });
  const [viewTick, setViewTick] = useState(0);
  const [viewBearing, setViewBearing] = useState(initialBearing || 0);

  const viewBearingRef = useRef(viewBearing);
  viewBearingRef.current = viewBearing;
  const onBearingChangeRef = useRef(onBearingChange);
  onBearingChangeRef.current = onBearingChange;
  const bearingSaveTimer = useRef(null);

  // Mode shortcuts: P(an)/E(dit)/D(raw)/A(ttach) — ignored while typing in a field (e.g. the
  // wall-length input) or with a modifier held, so they don't steal browser/OS shortcuts.
  useEffect(() => {
    const onKeyDown = e => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'p':
          setIntent('pan');
          onSelect(null);
          break;
        case 'e':
          setIntent('edit');
          break;
        case 'd':
          setIntent('draw');
          onSelect(null);
          break;
        case 'a':
          if (features.length) {
            setIntent('attach');
          }
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [features.length, onSelect]);

  const renderOrigin = moveDelta ? { lat: origin.lat + moveDelta.lat, lng: origin.lng + moveDelta.lng } : origin;

  const overlaySize = () => {
    const rect = wrap.current?.getBoundingClientRect();
    return rect && rect.width && rect.height ? { x: rect.width, y: rect.height } : { x: viewSize.w, y: viewSize.h };
  };

  const screen = p => {
    const map = mapRef.current;
    if (!map) {
      return { x: 0, y: 0 };
    }
    return toScreen(map, feetToLatLng(renderOrigin, p), overlaySize());
  };

  const toFeetFromClient = (clientX, clientY) => {
    const map = mapRef.current;
    const rect = wrap.current?.getBoundingClientRect();
    if (!map || !rect) {
      return { x: 0, y: 0 };
    }
    const pt = { x: clientX - rect.left, y: clientY - rect.top };
    return latLngToFeet(origin, fromScreen(map, pt, { x: rect.width, y: rect.height }));
  };

  const getPxPerFt = () => {
    const map = mapRef.current;
    if (!map) {
      return 10;
    }
    const size = overlaySize();
    const p0 = toScreen(map, feetToLatLng(origin, { x: 0, y: 0 }), size);
    const p1 = toScreen(map, feetToLatLng(origin, { x: 1, y: 0 }), size);
    return Math.max(0.5, Math.hypot(p1.x - p0.x, p1.y - p0.y));
  };

  useEffect(() => {
    if (!el.current || mapRef.current) {
      return undefined;
    }
    const map = L.map(el.current, {
      center: [center.lat, center.lng],
      zoom: zoomLevel || 19,
      maxZoom: 22,
      zoomSnap: 0.25,
      scrollWheelZoom: 'center',
      attributionControl: false,
      zoomControl: false,
      rotate: true,
      rotateControl: false,
      touchRotate: false,
      bearing: initialBearing || 0,
    });
    L.tileLayer('http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      maxZoom: 22,
      maxNativeZoom: 20,
      keepBuffer: 6,
    }).addTo(map);
    mapRef.current = map;
    map.dragging.disable();

    // Marks the property's known geocoded location (the policyholder address lat/long),
    // distinct from wherever the user pans/rotates the view while tracing. Visibility is
    // synced separately below so it can be toggled on/off without recreating the map.
    const centerDotHalo = L.circleMarker([center.lat, center.lng], {
      radius: 14,
      stroke: false,
      fillColor: '#4285f4',
      fillOpacity: 0.25,
      interactive: false,
    });
    const centerDot = L.circleMarker([center.lat, center.lng], {
      radius: 7,
      color: '#ffffff',
      weight: 2,
      fillColor: '#4285f4',
      fillOpacity: 1,
      interactive: false,
    });
    centerDotRef.current = centerDot;
    centerDotHaloRef.current = centerDotHalo;

    const bumpView = () => setViewTick(t => t + 1);
    map.on('move', bumpView);
    map.on('zoom', bumpView);

    const onRotate = () => {
      const b = ((map.getBearing() % 360) + 360) % 360;
      setViewBearing(b);
      if (bearingSaveTimer.current) {
        window.clearTimeout(bearingSaveTimer.current);
      }
      bearingSaveTimer.current = window.setTimeout(() => {
        if (onBearingChangeRef.current) {
          onBearingChangeRef.current(viewBearingRef.current);
        }
      }, 500);
    };
    map.on('rotate', onRotate);

    const measure = () => {
      const size = map.getSize();
      setViewSize({ w: size.x || 800, h: size.y || 600 });
      map.invalidateSize();
    };
    const raf1 = window.requestAnimationFrame(() => {
      measure();
      window.requestAnimationFrame(measure);
    });
    const t1 = window.setTimeout(measure, 400);
    const t2 = window.setTimeout(measure, 1200);
    const ro = new ResizeObserver(measure);
    if (el.current) {
      ro.observe(el.current);
    }

    return () => {
      window.cancelAnimationFrame(raf1);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      if (bearingSaveTimer.current) {
        window.clearTimeout(bearingSaveTimer.current);
      }
      ro.disconnect();
      map.off('move', bumpView);
      map.off('zoom', bumpView);
      map.off('rotate', onRotate);
      centerDot.remove();
      centerDotHalo.remove();
      centerDotRef.current = null;
      centerDotHaloRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle the address-location marker on/off without recreating the map.
  useEffect(() => {
    const map = mapRef.current;
    const dot = centerDotRef.current;
    const halo = centerDotHaloRef.current;
    if (!map || !dot || !halo) {
      return;
    }
    if (showCenterDot) {
      if (!map.hasLayer(halo)) {
        halo.addTo(map);
      }
      if (!map.hasLayer(dot)) {
        dot.addTo(map);
      }
    } else {
      map.removeLayer(halo);
      map.removeLayer(dot);
    }
  }, [showCenterDot]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (intent === 'pan') {
      map.dragging.enable();
    } else {
      map.dragging.disable();
    }
  }, [intent]);

  // The map is constructed once the tapeSketch record's persisted bearing may
  // not have loaded yet, so the constructor's own `bearing:` option can miss
  // it. Re-apply whenever the resolved prop changes so a late-arriving (or
  // externally changed) bearing still reorients the map without needing a
  // remount. The threshold guard keeps this from fighting the map's own
  // 'rotate' event, which round-trips the user's live rotation back through
  // this same prop.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof initialBearing !== 'number') {
      return;
    }
    const current = ((map.getBearing() % 360) + 360) % 360;
    const target = ((initialBearing % 360) + 360) % 360;
    if (Math.abs(current - target) > 0.5) {
      map.setBearing(initialBearing);
    }
  }, [initialBearing]);

  const glaLive = Math.round(
    features.reduce((s, f) => {
      const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
      const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
      return s + livingSqft(f, typeCode, garageKindName);
    }, 0),
  );

  const applyEdit = e => {
    const cur = editRef.current;
    if (!cur) {
      return;
    }
    const raw = toFeetFromClient(e.clientX, e.clientY);
    if (cur.kind === 'shiftAll') {
      const dx = raw.x - cur.start.x;
      const dy = raw.y - cur.start.y;
      const shifted = feetToLatLng(origin, { x: dx, y: dy });
      setMoveDelta({ lat: shifted.lat - origin.lat, lng: shifted.lng - origin.lng });
      return;
    }
    if (cur.kind === 'draft') {
      setDraft(p => {
        if (!p[cur.index]) {
          return p;
        }
        const next = [...p];
        const prev = next[cur.index - 1] ?? next[cur.index];
        next[cur.index] = roundPt(orthoSnap(prev, raw));
        return next;
      });
      return;
    }
    if (cur.kind === 'pending') {
      if (Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) < 12) {
        return;
      }
      if (cur.mode === 'vertex') {
        setEdit({ kind: 'vertex', id: cur.id, index: cur.index, orig: cur.orig });
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
        const snapped = roundPt(raw);
        onMove(
          cur.id,
          cur.orig.map((p, i) => (i === cur.index ? snapped : p)),
        );
      } else {
        setEdit({ kind: 'edge', id: cur.id, index: cur.index, start: cur.start, orig: cur.orig });
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
        onMove(cur.id, stretchEdge(cur.orig, cur.index, cur.start, raw));
      }
      return;
    }
    if (cur.kind === 'move') {
      // Body drags enter 'move' immediately on pointerdown (before any movement is confirmed),
      // unlike the 'pending'->vertex/edge transition above which is already gated by a movement
      // threshold — so this only calls onMoveStart once, on the first actual drag frame, via a
      // flag mutated directly on the (stable, per-drag) edit object rather than through setEdit.
      if (!cur.historyPushed) {
        cur.historyPushed = true;
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
      }
      const dx = Math.round(raw.x - cur.start.x);
      const dy = Math.round(raw.y - cur.start.y);
      onMove(
        cur.id,
        cur.orig.map(p => ({ x: p.x + dx, y: p.y + dy })),
      );
      return;
    }
    if (cur.kind === 'vertex') {
      const snapped = roundPt(raw);
      onMove(
        cur.id,
        cur.orig.map((p, i) => (i === cur.index ? snapped : p)),
      );
      return;
    }
    if (cur.kind === 'edge') {
      onMove(cur.id, stretchEdge(cur.orig, cur.index, cur.start, raw));
    }
  };

  const originPoint = () => {
    const last = draft[draft.length - 1];
    if (last) {
      return last;
    }
    return { x: 0, y: 0 };
  };

  const addWall = (d, ft) => {
    const n = Math.max(1, Math.round(ft));
    if (!(n > 0)) {
      return;
    }
    setDir(d);
    setIntent('draw');
    setDraft(p => {
      const start = p.length ? p[p.length - 1] : originPoint();
      const base = p.length ? p : [start];
      return [...base, step(start, d, n)];
    });
  };

  const addRect = () => {
    const w = Math.max(1, Math.round(Number(width)));
    const d = Math.max(1, Math.round(Number(depth)));
    if (!(w > 0 && d > 0)) {
      return;
    }
    const o = draft[0] ?? originPoint();
    onCommit([o, { x: o.x + w, y: o.y }, { x: o.x + w, y: o.y + d }, { x: o.x, y: o.y + d }]);
    setDraft([]);
  };

  const finish = () => {
    if (draft.length >= 3) {
      onCommit(draft);
      setDraft([]);
      setIntent('draw');
    }
  };

  const undo = () => {
    if (draft.length) {
      setDraft(p => p.slice(0, -1));
      return;
    }
    if (onUndoFeatures) {
      onUndoFeatures();
    }
  };

  const redo = () => {
    if (onRedoFeatures) {
      onRedoFeatures();
    }
  };

  const lastWall = draft.length >= 2 ? Math.round(Math.hypot(draft[draft.length - 1].x - draft[draft.length - 2].x, draft[draft.length - 1].y - draft[draft.length - 2].y)) : null;

  // eslint-disable-next-line no-unused-vars
  const _viewTickDep = viewTick; // referenced so render depends on pan/zoom, per JSX below

  return (
    <div className={classes.container}>
      <div className={classes.canvasArea}>
        <div ref={el} className={classes.map} />
        <div
          ref={wrap}
          className={classes.overlayWrap}
          style={{ pointerEvents: intent === 'pan' ? 'none' : 'auto' }}
          onWheel={e => {
            const map = mapRef.current;
            if (!map) {
              return;
            }
            e.preventDefault();
            map.setZoom(map.getZoom() + (e.deltaY < 0 ? 0.5 : -0.5));
          }}
          onPointerDown={e => {
            if (e.target.closest?.('[data-ui]')) {
              return;
            }
            if (e.button !== 0) {
              return;
            }
            const raw = toFeetFromClient(e.clientX, e.clientY);

            if (intent === 'move') {
              e.currentTarget.setPointerCapture(e.pointerId);
              setEdit({ kind: 'shiftAll', start: raw });
              return;
            }

            if (intent === 'attach') {
              const snap = snapToExisting(features, raw, getPxPerFt());
              if (snap) {
                setDraft([snap]);
                setIntent('draw');
              }
              return;
            }

            if (intent === 'edit') {
              const hit = hitFeature(features, screen, raw, getPxPerFt(), selectedId);
              if (hit) {
                e.currentTarget.setPointerCapture(e.pointerId);
                const orig = [...(features.find(f => f.id === hit.id)?.vertices ?? [])];
                if (hit.kind === 'body') {
                  onSelect(hit.id);
                  setEdit({ kind: 'move', id: hit.id, start: raw, orig });
                  return;
                }
                onSelect(hit.id);
                setEdit({
                  kind: 'pending',
                  id: hit.id,
                  mode: hit.kind,
                  index: hit.index,
                  start: raw,
                  orig,
                  snap: hit.snap,
                  sx: e.clientX,
                  sy: e.clientY,
                });
                return;
              }
              onSelect(null);
              return;
            }

            // intent === 'draw' — existing shapes are purely visual here (not hit-tested),
            // matching Pan/Draw/Move being inert modes for editing.
            if (draft.length > 0) {
              const pxPerFt = getPxPerFt();
              const lastPt = draft[draft.length - 1];
              const s = screen(lastPt);
              const rect = wrap.current?.getBoundingClientRect();
              const x = e.clientX - (rect?.left ?? 0);
              const y = e.clientY - (rect?.top ?? 0);
              if (Math.hypot(x - s.x, y - s.y) <= 16) {
                e.currentTarget.setPointerCapture(e.pointerId);
                setEdit({ kind: 'draft', index: draft.length - 1 });
                return;
              }
              if (draft.length >= 3) {
                const first = screen(draft[0]);
                if (Math.hypot(x - first.x, y - first.y) <= 18) {
                  finish();
                  return;
                }
              }
              const attach = snapToExisting(features, raw, pxPerFt);
              const next = attach ?? roundPt(orthoSnap(lastPt, raw));
              if (next.x === lastPt.x && next.y === lastPt.y) {
                return;
              }
              if (draft.length >= 3 && Math.hypot(next.x - draft[0].x, next.y - draft[0].y) <= 2.5) {
                finish();
                return;
              }
              setDraft(p => [...p, next]);
              return;
            }
            setDraft([roundPt(raw)]);
          }}
          onPointerMove={applyEdit}
          onPointerUp={() => {
            const cur = editRef.current;
            if (cur?.kind === 'shiftAll' && moveDelta) {
              if (Math.abs(moveDelta.lat) > 1e-9 || Math.abs(moveDelta.lng) > 1e-9) {
                if (onShiftAll) {
                  onShiftAll(moveDelta.lat, moveDelta.lng);
                }
              }
              setMoveDelta(null);
            }
            setEdit(null);
          }}
        >
          <svg className={classes.svg} width={viewSize.w} height={viewSize.h}>
            {features.map(f => {
              const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
              const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
              const foundationCode = (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '';
              const pts = (f.vertices || []).map(screen);
              if (pts.length < 3) {
                return null;
              }
              const d = `${pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')} Z`;
              const lens = edgeLengths(f.vertices || []);
              const others = features.filter(x => x.id !== f.id).map(x => (x.vertices || []).map(screen));
              const c = labelAnchor(pts, others);
              const fp = Math.round(measureSqft(f.vertices));
              const liv = Math.round(livingSqft(f, typeCode, garageKindName));
              const isSelected = f.id === selectedId && intent === 'edit';
              const color = TYPE_COLORS[typeCode] || TYPE_COLORS.OTH;
              return (
                <g key={f.id} data-feat={f.id}>
                  <path d={d} fill={color} fillOpacity={isSelected ? 0.4 : 0.22} stroke={color} strokeWidth={isSelected ? 2.5 : 1.75} />
                  {(f.vertices || []).map((a, i) => {
                    const b = (f.vertices || [])[(i + 1) % (f.vertices?.length ?? 1)];
                    const mid = screen({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                    return (
                      <g key={i}>
                        <text x={mid.x} y={mid.y - 10} fill="#fff" fontSize="11" fontFamily="IBM Plex Mono, monospace" textAnchor="middle" style={{ paintOrder: 'stroke', stroke: '#000', strokeWidth: 3 }}>
                          {`${Math.round(lens[i])}'`}
                        </text>
                        {isSelected && <circle cx={mid.x} cy={mid.y} r="5" fill="#ecece8" stroke="#0e1012" strokeWidth="1.25" />}
                      </g>
                    );
                  })}
                  {isSelected &&
                    pts.map((p, i) => <rect key={`v${i}`} x={p.x - 5} y={p.y - 5} width="10" height="10" fill="#ecece8" stroke="#0e1012" strokeWidth="1.25" />)}
                  <text x={c.x} y={c.y} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontFamily="IBM Plex Sans, sans-serif" style={{ paintOrder: 'stroke', stroke: '#000', strokeWidth: 3 }}>
                    <tspan x={c.x} dy="-0.65em" fontSize="12" fontWeight="600">
                      {sectionAbbrev(f, typeCode, foundationCode, garageKindName)}
                    </tspan>
                    <tspan x={c.x} dy="1.4em" fontSize="11" fontFamily="IBM Plex Mono, monospace">
                      {liv ? `${liv.toLocaleString()} GLA` : `${fp.toLocaleString()} SF`}
                    </tspan>
                  </text>
                </g>
              );
            })}
            {draft.length > 0 && (
              <g>
                <polyline
                  fill="none"
                  stroke="#c8ccd4"
                  strokeWidth="2"
                  points={draft
                    .map(p => {
                      const s = screen(p);
                      return `${s.x},${s.y}`;
                    })
                    .join(' ')}
                />
                {draft.map((p, i) => {
                  const s = screen(p);
                  const lastPt = i === draft.length - 1;
                  return (
                    <circle
                      key={i}
                      cx={s.x}
                      cy={s.y}
                      r={lastPt ? 7 : i === 0 ? 5 : 3.5}
                      fill={lastPt ? '#ecece8' : '#c8ccd4'}
                      stroke="#0e1012"
                      strokeWidth={lastPt ? 1.5 : 0}
                    />
                  );
                })}
                {draft.length > 1 &&
                  draft.slice(0, -1).map((a, i) => {
                    const b = draft[i + 1];
                    const mid = screen({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                    const len = Math.hypot(b.x - a.x, b.y - a.y);
                    return (
                      <text key={i} x={mid.x} y={mid.y - 8} fill="#ecece8" fontSize="12" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
                        {`${Math.round(len)}'`}
                      </text>
                    );
                  })}
              </g>
            )}
          </svg>
          {intent === 'edit' &&
            selectedId &&
            (() => {
              const sel = features.find(f => f.id === selectedId);
              const pts = (sel?.vertices ?? []).map(screen);
              if (pts.length < 3) {
                return null;
              }
              const left = Math.max(...pts.map(p => p.x)) + 6;
              const top = Math.min(...pts.map(p => p.y)) - 50;
              return (
                <IconButton
                  data-ui="delete"
                  aria-label="Delete section"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => {
                    e.stopPropagation();
                    onDelete(selectedId);
                  }}
                  className={classes.deleteBtn}
                  style={{ left, top }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              );
            })()}
        </div>
        <div className={classes.topOverlay}>
          <div className={classes.hud}>
            {lastWall != null
              ? `${lastWall}'`
              : intent === 'pan'
                ? 'Pan'
                : intent === 'edit'
                  ? 'Edit — drag a vertex, edge, or shape'
                  : intent === 'move'
                    ? 'Move All — drag anywhere to shift the whole sketch'
                    : intent === 'attach'
                      ? 'Tap a wall'
                      : 'Draw'}
            {glaLive ? `  ·  ${glaLive.toLocaleString()} GLA` : ''}
          </div>
        </div>
        <div data-ui="zoom" className={classes.zoomBar}>
          <IconButton size="small" onPointerDown={e => e.stopPropagation()} onClick={() => mapRef.current?.zoomIn()} aria-label="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onPointerDown={e => e.stopPropagation()} onClick={() => mapRef.current?.zoomOut()} aria-label="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <div className={classes.controlDivider} />
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => {
              const map = mapRef.current;
              if (map) {
                map.setBearing((((map.getBearing() - 5) % 360) + 360) % 360);
              }
            }}
            aria-label="Rotate left"
          >
            <RotateLeftIcon fontSize="small" />
          </IconButton>
          <span className={classes.bearingReadout}>{Math.round(viewBearing)}°</span>
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => {
              const map = mapRef.current;
              if (map) {
                map.setBearing((map.getBearing() + 5) % 360);
              }
            }}
            aria-label="Rotate right"
          >
            <RotateRightIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => {
              const map = mapRef.current;
              if (map) {
                map.setBearing(0);
              }
            }}
            aria-label="Reset to north"
            title="Reset to north"
          >
            <ExploreIcon fontSize="small" />
          </IconButton>
          <div className={classes.controlDivider} />
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => setShowCenterDot(v => !v)}
            aria-label={showCenterDot ? 'Hide address marker' : 'Show address marker'}
            title={showCenterDot ? 'Hide address marker' : 'Show address marker'}
          >
            {showCenterDot ? <LocationOnIcon fontSize="small" /> : <LocationOffIcon fontSize="small" />}
          </IconButton>
        </div>
        <div className={classes.northIndicator} style={{ transform: `rotate(${-viewBearing}deg)` }}>
          <ExploreIcon fontSize="small" />
        </div>
      </div>
      <TapeDrawPad
        modeBar={
          <div className={classes.modeCols}>
            <div className={classes.modeCol}>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'pan' && classes.intentButtonActive)}
                onClick={() => {
                  setIntent('pan');
                  onSelect(null);
                }}
                startIcon={<PanToolIcon fontSize="small" />}
                title="Pan (P)"
              >
                Pan
              </Button>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'edit' && classes.intentButtonActive)}
                onClick={() => setIntent('edit')}
                startIcon={<OpenWithIcon fontSize="small" />}
                title="Edit (E)"
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!features.length}
                className={cn(classes.intentButton, intent === 'move' && classes.intentButtonActive)}
                onClick={() => setIntent('move')}
                startIcon={<ControlCameraIcon fontSize="small" />}
              >
                Move All
              </Button>
            </div>
            <div className={classes.modeCol}>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'draw' && classes.intentButtonActive)}
                onClick={() => {
                  setIntent('draw');
                  onSelect(null);
                }}
                startIcon={<CreateIcon fontSize="small" />}
                title="Draw (D)"
              >
                Draw
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!features.length}
                className={cn(classes.intentButton, intent === 'attach' && classes.intentButtonActive)}
                onClick={() => setIntent('attach')}
                startIcon={<AddIcon fontSize="small" />}
                title="Add onto wall (A)"
              >
                Add onto wall
              </Button>
            </div>
          </div>
        }
        dir={dir}
        onDir={setDir}
        length={length}
        onLength={setLength}
        width={width}
        depth={depth}
        onWidth={setWidth}
        onDepth={setDepth}
        onAddWall={addWall}
        onUndo={undo}
        onRedo={redo}
        onClose={finish}
        canClose={draft.length >= 3}
        onPlaceRect={addRect}
        onClear={() => {
          setDraft([]);
          onClearFeatures();
        }}
      />
    </div>
  );
};

AerialCanvas.propTypes = {
  center: PropTypes.object.isRequired,
  origin: PropTypes.object.isRequired,
  zoomLevel: PropTypes.number,
  initialBearing: PropTypes.number,
  features: PropTypes.array,
  selectedId: PropTypes.string,
  tapeConfig: PropTypes.object,
  onCommit: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onMoveStart: PropTypes.func,
  onClearFeatures: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUndoFeatures: PropTypes.func,
  onRedoFeatures: PropTypes.func,
  onShiftAll: PropTypes.func,
  onBearingChange: PropTypes.func,
};

export default AerialCanvas;
